import { ColumnType } from "antd/es/table";
import { Employee } from "../../types/employee.type";
import DateTime from "../../components/DateTime";
import ZupRoleFilterDropdown from "../../features/integration-setting-table/RolesTable/ZupRoleFilterDropdown";
import { FilterDropdownProps } from "antd/es/table/interface";

type GetColumnsParams = {
  employee?: Employee[];
  openFilterKey: string | null;
  setOpenFilterKey: (key: string | null) => void;
  getColumnDataPickerProps: () => any;
  getColumnSearchProps: (placeholder: string, dataIndex: keyof Employee) => any;
  zupFilter: string[] | undefined;
  setZupFilter: (keys: string[] | undefined) => void;
  setSearchParams: React.Dispatch<any>;
  setSearchRolesPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setSearchDepartmentPageNumber: React.Dispatch<React.SetStateAction<number>>;
  iikoRoles?: { name: string; code: string }[];
  iikoFilter: string[] | undefined;
  zupDepartment?: { name: string; id: string }[];
  iikoDepartment?: { name: string; code: string }[];
  iSpringDepartment?: { name: string; id: string }[];
};

export const getEmployeeTableColumns = ({
  employee,
  openFilterKey,
  setOpenFilterKey,
  getColumnDataPickerProps,
  getColumnSearchProps,
  zupFilter,
  setZupFilter,
  setSearchParams,
  setSearchRolesPageNumber,
  setSearchDepartmentPageNumber,
  iikoRoles,
  iikoFilter,
  zupDepartment,
  iikoDepartment,
  iSpringDepartment,
}: GetColumnsParams): ColumnType<Employee>[] => {
  const generateFilters = (data: Employee[] | undefined, dataIndex: keyof Employee) => {
    if (!data) return [];
    const filterSet = new Set<string>();
    data.forEach((item) => {
      const value = item[dataIndex];
      if (value) filterSet.add(value.toString());
    });
    return Array.from(filterSet).map((filter) => ({ text: filter, value: filter }));
  };

  return [
    {
      title: "ИД",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Дата изменения",
      dataIndex: "updated_date",
      key: "updated_date",
      render: (updated_date: string) => <DateTime dateTime={updated_date} />,
      filterDropdownOpen: openFilterKey === "updated_date",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "updated_date" : null);
      },
      ...getColumnDataPickerProps(),
    },
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      filters: generateFilters(employee, "name"),
      filterDropdownOpen: openFilterKey === "name",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "name" : null);
      },
      filterSearch: true,
      onFilter: (value: string, record: Employee) =>
        record.name.toLowerCase().includes((value as string).toLowerCase()),
      ...getColumnSearchProps("Введите имя", "name"),
    },
    {
      title: "ПИНДЛ",
      dataIndex: "pinfl",
      key: "pinfl",
      filters: generateFilters(employee, "pinfl"),
      filterDropdownOpen: openFilterKey === "pinfl",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "pinfl" : null);
      },
      filterSearch: true,
      onFilter: (value: string, record: Employee) =>
        (record.pinfl ?? "").toLowerCase().includes((value as string).toLowerCase()),
      ...getColumnSearchProps("Введите ПИНДЛ", "pinfl"),
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
      filters: generateFilters(employee, "phone"),
      filterDropdownOpen: openFilterKey === "phone",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "phone" : null);
      },
      filterSearch: true,
      onFilter: (value: string, record: Employee) => record.phone.includes(value as string),
      ...getColumnSearchProps("Введите Телефон", "phone"),
    },
    {
      title: "Роль в 1С",
      dataIndex: "oneczup_role_name",
      key: "oneczup_role_name",
      filterDropdownOpen: openFilterKey === "oneczup_role_name",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "oneczup_role_name" : null);
      },
      filterSearch: true,
      onFilter: () => true,
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <ZupRoleFilterDropdown
          selectedKeys={selectedKeys as string[]}
          setSelectedKeys={(keys) => {
            clearFilters?.();
            setSelectedKeys(keys);
            setZupFilter(keys);
            setSearchRolesPageNumber(1);
            setSearchParams((prev: any) => ({
              ...prev,
              page_number: 1,
              oneczup_role_ids: keys.length ? keys : undefined,
            }));
            confirm({ closeDropdown: false });
          }}
          confirm={confirm}
          clearFilters={() => {
            clearFilters?.();
            setZupFilter([]);
            setSearchParams((prev: any) => ({
              ...prev,
              page_number: 1,
              oneczup_role_ids: undefined,
            }));
          }}
        />
      ),
      filteredValue: zupFilter,
    },
    {
      title: "Роль в IIKO",
      dataIndex: "iiko_role_name",
      key: "iiko_role_name",
      filters: iikoRoles?.map((role) => ({ text: role.name, value: role.code })) || [],
      filteredValue: iikoFilter,
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "iiko_role_name" : null);
      },
      filterSearch: true,
      onFilter: () => true,
    },
    {
      title: "Роль в iSpring",
      dataIndex: "ispring_role_name",
      key: "ispring_role_name",
    },
    {
      title: "Отдел в 1С",
      dataIndex: "oneczup_department_name",
      key: "oneczup_department_name",
      filters: zupDepartment?.map((d) => ({ text: d.name, value: d.id })) || [],
      filterDropdownOpen: openFilterKey === "oneczup_department_name",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "oneczup_department_name" : null);
      },
      filterSearch: true,
      onFilter: () => {
        setSearchDepartmentPageNumber(1);
        return true;
      },
    },
    {
      title: "Отдел в IIKO",
      dataIndex: "iiko_department_name",
      key: "iiko_department_name",
      filters: iikoDepartment?.map((d) => ({ text: d.name, value: d.code })) || [],
      filterDropdownOpen: openFilterKey === "iiko_department_name",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "iiko_department_name" : null);
      },
      filterSearch: true,
      onFilter: () => {
        setSearchDepartmentPageNumber(1);
        return true;
      },
    },
    {
      title: "Отдел в iSpring",
      dataIndex: "ispring_department_name",
      key: "ispring_department_name",
      filters: iSpringDepartment?.map((d) => ({ text: d.name, value: d.id })) || [],
      filterDropdownOpen: openFilterKey === "ispring_department_name",
      onFilterDropdownVisibleChange: (visible: any) => {
        setOpenFilterKey(visible ? "ispring_department_name" : null);
      },
      filterSearch: true,
      onFilter: () => {
        setSearchDepartmentPageNumber(1);
        return true;
      },
    },
  ];
};
